import Nav from "@/components/Nav";
import PageHero from "@/components/PageHero";
import MarketingFooter from "@/components/MarketingFooter";
import { Link } from "react-router-dom";
import { Users, ShieldCheck, MessageCircle, Share2, Check, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const FEATURES = [
  {
    icon: ShieldCheck,
    title: "Permission-based, revocable",
    body: "Invite up to 4 family members — parents, elders, trusted mentors — with a single tap. They see only what you allow. You can revoke access anytime with one click. No one is ever forced in; no one is ever locked in.",
  },
  {
    icon: Share2,
    title: "Family Fit — a section written for elders",
    body: "Every Marriage Intelligence Report includes a dedicated Family Fit section — written in the language elders understand. Family values, generational continuity, community alignment, joint-family readiness. Because in India, marriage isn't two people. It's two families.",
  },
  {
    icon: MessageCircle,
    title: "Guided pre-marriage conversations",
    body: "Every report ends with a short list of Recommended Conversations — specific, thoughtful questions to have with your match before you say yes. Families can co-read these prompts and start the harder conversations earlier, in a healthier way.",
  },
  {
    icon: Users,
    title: "Family-shareable PDF reports",
    body: "Download the report as a beautifully designed PDF. Share with the extended family via WhatsApp. Or send a view-only link elders can open without creating an account. No sign-ups, no friction — just clarity.",
  },
];

const TIER_INFO = [
  { tier: "Discovery (Free)", family: "Not available", tone: "muted" },
  { tier: "Clarity (₹1,999/mo)", family: "PDF share + public view-link", tone: "muted" },
  { tier: "Journey (₹4,999/mo)", family: "Full Family Circle · 4 members · in-app view", tone: "gold" },
];

export default function Family() {
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav variant="marketing" />
      <PageHero
        overline="Family Circle"
        sanskrit="कुटुम्ब · आशीर्वाद · स्पष्टता"
        title={<>The decision is yours.<br /><span className="italic text-amber-300">The blessing is theirs.</span></>}
        subtitle="No dating app treats family as a first-class user. No matrimonial portal reads for family fit. Anubandh.ai does both — because in India, marriage isn't a couple's decision, it's a family's blessing."
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/subscription" data-testid="family-plans-cta" className="btn-pill bg-amber-500 text-[#12103A] text-sm px-6 py-3 font-medium inline-flex items-center gap-2">
            See Journey Plan <ArrowRight size={16} />
          </Link>
          <Link to="/how-it-works" className="btn-pill border border-white/25 text-white text-sm px-6 py-3 hover:bg-white/10">How it works</Link>
        </div>
      </PageHero>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="overline">A first-class user</div>
          <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1E1B4B] mt-2 leading-tight">
            Every feature designed for the elders in the room.
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="rounded-2xl border border-stone-200 bg-white p-7"
            >
              <f.icon className="text-amber-700" size={22} />
              <h3 className="font-serif-display text-2xl text-[#1E1B4B] mt-4">{f.title}</h3>
              <p className="mt-3 text-stone-700 leading-relaxed text-[15px]">{f.body}</p>
            </motion.div>
          ))}
        </div>

        {/* Tier comparison */}
        <div className="mt-20">
          <div className="overline">Family Circle across plans</div>
          <h3 className="font-serif-display text-3xl text-[#1E1B4B] mt-2">
            Every plan includes family — Journey unlocks the full Circle.
          </h3>
          <div className="mt-8 border border-stone-200 rounded-2xl bg-white overflow-hidden">
            {TIER_INFO.map((t, i) => (
              <div
                key={t.tier}
                className={`flex items-center justify-between p-6 ${i > 0 ? "border-t border-stone-100" : ""} ${t.tone === "gold" ? "bg-amber-50" : ""}`}
              >
                <div>
                  <div className={`font-serif-display text-xl ${t.tone === "gold" ? "text-[#1E1B4B]" : "text-[#1E1B4B]"}`}>{t.tier}</div>
                  <div className="text-sm text-stone-600 mt-1">{t.family}</div>
                </div>
                {t.tone === "gold" && (
                  <div className="hidden sm:flex items-center gap-1 text-xs uppercase tracking-widest text-amber-700 bg-white border border-amber-300 rounded-full px-3 py-1">
                    <Check size={12} /> Full Family Circle
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Cultural note */}
        <div className="mt-16 rounded-2xl p-10 relative overflow-hidden text-white text-center" style={{ background: "#12103A" }}>
          <div className="devanagari text-amber-300 text-2xl mb-4">यत्र नार्यस्तु पूज्यन्ते, रमन्ते तत्र देवताः</div>
          <p className="font-serif-display text-2xl lg:text-3xl italic max-w-3xl mx-auto leading-relaxed">
            &quot;Where women are honoured, there the divine rejoices.&quot;
          </p>
          <p className="mt-6 text-stone-400 text-sm max-w-xl mx-auto">
            Family Circle is our quiet way of honouring the elders — while giving the individual
            complete control. Both, at the same time.
          </p>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
